// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Info popover explaining how a document lands in a given match bucket.
 *
 * The rules mirror the backend scorer (_compute_match / _build_hr_match in
 * i9processor/status_handler.py): the I-9's four OCR'd fields — SSN, first
 * name, last name, date of birth — are scored against the matched HR person.
 */
import type { FC } from 'react'
import { Info } from 'lucide-react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui'

type MatchBucket = 'matched' | 'partial_match' | 'no_match'

const RULES: Record<MatchBucket, { title: string; lines: string[] }> = {
  matched: {
    title: 'Fully Matched',
    lines: [
      'The SSN read from the I-9 matches an HR record, and',
      'at least one other field (first name, last name, or date of birth) also matches, and',
      'the HR lookup found exactly one person.',
    ],
  },
  partial_match: {
    title: 'Partial Match',
    lines: [
      'A person was found in HR, but the match is not conclusive. Any one of these lands here:',
      'the SSN did not match (e.g. the person was found only by name),',
      'no name or date-of-birth field corroborated the SSN,',
      'the lookup returned more than one possible person (ambiguous).',
    ],
  },
  no_match: {
    title: 'No Match',
    lines: ['HR was searched by both SSN and name, and no matching person was found at all.'],
  },
}

interface MatchRulesInfoProps {
  bucket: MatchBucket
}

export const MatchRulesInfo: FC<MatchRulesInfoProps> = ({ bucket }) => {
  const rule = RULES[bucket]
  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* A span (not a button) so it can nest inside the TabsTrigger button; stop
            propagation so opening the popover doesn't also switch tabs. */}
        <span
          role="button"
          tabIndex={0}
          aria-label={`${rule.title} matching rules`}
          className="inline-flex cursor-pointer opacity-60 hover:opacity-100"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Info className="h-3.5 w-3.5" />
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="text-sm font-semibold">{rule.title}</div>
        <p className="mt-1 text-xs text-muted-foreground">{rule.lines[0]}</p>
        {rule.lines.length > 1 && (
          <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-muted-foreground">
            {rule.lines.slice(1).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  )
}
